import { StandardEvents } from '@shopify/events';

/**
 * Renders a complementary-products upsell in the cart, based on the most recently
 * added cart line item. Intentionally self-contained: it fetches its own data and
 * only ever touches its own subtree, rather than going through the section-wide
 * `morphSection` hydration flow.
 *
 * That flow still reaches this element from the outside, though: it's nested inside
 * `.cart-drawer__inner` (`data-hydration-key="cart-drawer-inner"`), and ANY section
 * refresh of that ancestor — the drawer's own hydration, a quantity change, or the
 * `sections` param on a normal add-to-cart request — does a plain (non-hydration)
 * morph of that whole subtree. That morph patches in place rather than replacing
 * nodes wholesale, so it can reset our content back to the server's default
 * (unhydrated/skeleton) markup without ever changing this element's own direct
 * children — the MutationObserver below watches the whole subtree specifically so it
 * still catches that, regardless of which trigger caused it or how it races our own
 * updates.
 */
class CartUpsell extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {boolean} */
  #isSelfUpdate = false;

  /** @type {boolean} */
  #hasResolvedContent = false;

  /**
   * Product ids removed optimistically on submit (see #handleAddSubmit), so the
   * cartLinesUpdate confirmation that follows — which can arrive a second or more
   * later, well after the section-wide refresh described above has already reset our
   * markup — knows the card is already gone and doesn't undo that by refetching for it.
   * @type {Set<string>}
   */
  #optimisticallyRemoved = new Set();

  connectedCallback() {
    // The cart drawer is closed (and typically not laid out) at first paint, so the
    // intersection check can miss the moment it opens. The drawer's own open event is
    // the reliable trigger; intersection observation is kept as a secondary safety net
    // (e.g. for the full cart page, where this renders inline and is already visible).
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        this.#intersectionObserver?.disconnect();
        this.#refresh();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#intersectionObserver.observe(this);

    this.#mutationObserver = new MutationObserver(this.#handleMutation);
    this.#mutationObserver.observe(this, { childList: true, subtree: true });

    this.addEventListener('submit', this.#handleAddSubmit);
    this.closest('theme-drawer')?.addEventListener('theme-drawer:open', this.#handleDrawerOpen);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  disconnectedCallback() {
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.removeEventListener('submit', this.#handleAddSubmit);
    this.closest('theme-drawer')?.removeEventListener('theme-drawer:open', this.#handleDrawerOpen);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
  }

  #handleDrawerOpen = () => {
    this.#refresh();
  };

  /**
   * Fires on the form's native `submit` event. `<product-form-component>`'s own submit
   * handling is wired up as a delegated listener on `document` (see `component.js`),
   * which — being further from the target than us — only runs *after* this one in the
   * bubble phase. So the removal below is deferred a tick, to land after that handler
   * (and the add-to-cart request it starts) rather than racing it out of existence.
   * @param {SubmitEvent} event
   */
  #handleAddSubmit = (event) => {
    const form = /** @type {HTMLElement} */ (event.target);
    const item = form.closest?.('.cart-upsell__item');
    const productId = item?.dataset.productId;
    if (!productId) return;

    setTimeout(() => {
      // Removed as soon as the request has started, without waiting for it to resolve —
      // waiting loses the race against the section-wide refresh that same request
      // triggers elsewhere (see class doc), which resets this element first.
      this.#optimisticallyRemoved.add(productId);
      this.#removeItem(productId);
    });
  };

  /** @param {import('@shopify/events').CartLinesUpdateEvent} event */
  #handleCartLinesUpdate = (event) => {
    // `cart.items` isn't reliably ordered by recency (e.g. bundle/combo line items can
    // reorder it), so we can't just take the last entry. The event's own promise resolves
    // with `detail.productId` — the exact product this specific mutation was for — which
    // is what "the product that was just added" actually means.
    event.promise
      ?.then(({ detail }) => {
        const productId = detail?.productId != null ? String(detail.productId) : undefined;

        if (productId && this.#optimisticallyRemoved.delete(productId)) {
          // Already handled at submit time — the confirmation changes nothing further.
          return;
        }

        // A cart change we didn't cause ourselves (e.g. added elsewhere on the site) —
        // drop the matching card if we're showing it, otherwise get fresh recommendations.
        if (productId && this.#removeItem(productId)) return;
        this.#refresh(productId);
      })
      .catch(() => {
        this.#refresh();
      });
  };

  #handleMutation = () => {
    if (this.#isSelfUpdate) return;
    if (!this.#hasResolvedContent) return;

    // With subtree observation on, this fires for all sorts of harmless descendant
    // changes too (an add-to-cart button's own "added!" animation, etc). Only react to
    // the one specific thing we're actually defending against: the server's default
    // unhydrated skeleton markup landing back in place of content we'd already resolved.
    if (!this.querySelector('.cart-upsell__item--skeleton')) return;

    this.#hasResolvedContent = false;
    this.#refresh();
  };

  /**
   * Removes the card for a given product, if one of ours is showing it.
   * @param {string} productId
   * @returns {boolean} Whether a matching card was found and removed.
   */
  #removeItem(productId) {
    const item = this.querySelector(`.cart-upsell__item[data-product-id="${productId}"]`);
    if (!item) return false;

    if (this.querySelectorAll('.cart-upsell__item').length === 1) {
      // That was the last card — clear everything so `.cart-upsell:empty` hides the
      // now-headless section instead of leaving a heading over an empty list.
      this.#setContent('');
      return true;
    }

    // Guarded like #setContent: the observer watches the whole subtree (see
    // connectedCallback), so removing this nested <li> is itself a mutation it'd see.
    this.#isSelfUpdate = true;
    item.remove();
    queueMicrotask(() => {
      this.#isSelfUpdate = false;
    });
    return true;
  }

  /**
   * @param {string | number} [knownProductId] - Skip the cart lookup when the caller
   *   already knows exactly which product this refresh is for (see #handleCartLinesUpdate).
   */
  async #refresh(knownProductId) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();
    const myController = this.#activeFetch;
    const { signal } = myController;

    try {
      let productId = knownProductId;

      if (!productId) {
        const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
          signal,
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) return;

        /** @type {{ items: Array<{ product_id: number }> }} */
        const cart = await cartResponse.json();
        productId = cart.items[cart.items.length - 1]?.product_id;
      }

      if (!productId) {
        this.#setContent('');
        return;
      }

      const url = `${this.dataset.url}&product_id=${productId}&section_id=cart-drawer-section&intent=complementary`;
      const response = await fetch(url, { signal });
      if (!response.ok) return;

      const html = await response.text();
      const newContent = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);

      if (newContent) {
        this.#setContent(newContent.innerHTML);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Cart upsell error:', error);
    } finally {
      // Another #refresh() call may have superseded this one (and already reset this
      // to its own controller) — only clear it if it's still ours to clear.
      if (this.#activeFetch === myController) {
        this.#activeFetch = null;
      }
    }
  }

  /**
   * Sets the element's content, marking the mutation as our own so #handleMutation
   * doesn't mistake it for an external reset (see class doc).
   * @param {string} html
   */
  #setContent(html) {
    this.#isSelfUpdate = true;
    this.innerHTML = html;
    this.#hasResolvedContent = true;
    queueMicrotask(() => {
      this.#isSelfUpdate = false;
    });
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
